import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/cn';
import { useFormatters } from '@shared/lib/i18n/formatters';

export const HIFZ_STATES = ['not_started', 'learning', 'memorized', 'needs_revision', 'mastered'] as const;
export type HifzState = (typeof HIFZ_STATES)[number];

export interface JuzProgress {
  /** 1–30 */
  juz: number;
  state: HifzState;
  /** 0–100, share of the juz's ayahs memorized or better. */
  percent: number;
}

/** Colour + glyph + border on every cell — never colour alone (a11y doc §5). */
const CELL: Record<HifzState, { className: string; glyph: string }> = {
  not_started: { className: 'bg-hifz-none text-ink-950 border border-ink-300', glyph: '○' },
  learning: { className: 'bg-hifz-learning text-ink-950 border border-gold-600', glyph: '◔' },
  memorized: { className: 'bg-hifz-memorized text-ink-950 border border-primary-600', glyph: '●' },
  needs_revision: { className: 'bg-hifz-weak text-ink-950 border border-danger', glyph: '↻' },
  mastered: { className: 'hifz-cell-mastered text-paper-0', glyph: '★' },
};

export function hifzGlyph(state: HifzState): string {
  return CELL[state].glyph;
}

/** Fill in missing juz as not started so the map always has 30 cells. */
export function normalizeJuzProgress(data: readonly JuzProgress[]): JuzProgress[] {
  const byJuz = new Map(data.map((d) => [d.juz, d]));
  return Array.from({ length: 30 }, (_, i) => byJuz.get(i + 1) ?? { juz: i + 1, state: 'not_started', percent: 0 });
}

export interface JuzMapProps {
  data: readonly JuzProgress[];
  /** Accessible name of the map, e.g. "Hifz progress by juz". */
  label?: string;
  showLegend?: boolean;
  className?: string;
}

/**
 * 30 juz cells coloured by the --hifz-* tokens. Cells flow start → end, so in Arabic Juz 1 sits at
 * the right (reading order). Each cell carries an accessible label with state and percentage.
 */
export function JuzMap({ data, label, showLegend = true, className }: JuzMapProps) {
  const { t } = useTranslation('common');
  const f = useFormatters();
  const cells = normalizeJuzProgress(data);
  const mapLabel = label ?? t('juzMap.label');

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <ol aria-label={mapLabel} className="grid grid-cols-5 gap-2 sm:grid-cols-6 md:grid-cols-10">
        {cells.map((cell) => {
          const spec = CELL[cell.state];
          const percent = f.percent(Math.round(cell.percent));
          const stateLabel = t(`status.hifz.${cell.state}` as 'status.hifz.learning');
          return (
            <li
              key={cell.juz}
              aria-label={t('juzMap.cell', { juz: f.number(cell.juz), state: stateLabel, percent })}
              className={cn('flex aspect-square flex-col items-center justify-center gap-0.5 rounded-md p-1', spec.className)}
            >
              <span aria-hidden="true" className="tabular text-md font-semibold leading-none">
                {f.number(cell.juz)}
              </span>
              <span aria-hidden="true" className="text-xs leading-none">
                {spec.glyph}
              </span>
              <span aria-hidden="true" className="tabular text-[0.65rem] leading-none opacity-90">
                {percent}
              </span>
            </li>
          );
        })}
      </ol>
      {showLegend ? (
        <ul aria-label={t('juzMap.legend')} className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-secondary">
          {(Object.keys(CELL) as HifzState[]).map((state) => (
            <li key={state} className="flex items-center gap-2">
              <span aria-hidden="true" className={cn('flex size-5 items-center justify-center rounded-sm text-xs', CELL[state].className)}>
                {CELL[state].glyph}
              </span>
              {t(`status.hifz.${state}` as 'status.hifz.learning')}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
